import type React from 'react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useMatch } from 'react-router-dom';

import { Separator } from '~/components/Separator';
import { useUser } from '~/modules/auth/hooks/useUser';
import { BrandContext } from '~/modules/branding';
import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';
import { HoverIconItem } from '~/modules/navigation/components/Sidebar/parts/HoverIconItem';
import {
  USER_MANAGEMENT_PERMISSION,
  WMG1_APP_NAME,
} from '~/modules/user-management/constants';
import { dataTestIds } from '~/test-utils/dataTestIds';

const SUPPORT_URL = 'https://warnermusic.service-now.com/adahub?id=coop_faq';

export const WMG_FEEDBACK_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLSefQ1_QjxKfrYjKxX0fDwgOr8iwGy8ZH0wIAO6Gz6HlU0YvJw/viewform?usp=sf_link';
export const WMG_SUPPORT_URL =
  'https://docs.google.com/document/d/1jSWAS6HTCA68ccCRBOqo7MXHVYatskeqj8OzrUk8Qko/';

interface SidebarPopoverContentProps {
  name: string;
  onItemClick?: (event?: React.MouseEvent<HTMLElement>) => void;
}

export function SidebarPopoverContent({
  name,
  onItemClick,
}: SidebarPopoverContentProps) {
  const disabledRoutes = [
    useMatch('/video-management/*'),
    useMatch('/settings/*'),
  ];
  const isFeedbackDisabledRoute = disabledRoutes.some(Boolean);
  const brand = useContext(BrandContext);

  const [isContainerProfileEnabled, isUserManagementEnabled] = useFeatureFlags([
    FEATURE_FLAGS.ContainerProfile,
    FEATURE_FLAGS.WmgUserManagement,
  ]);

  const hasUserManagementReadPermission = useUser().hasPermission(
    USER_MANAGEMENT_PERMISSION,
    undefined,
    WMG1_APP_NAME,
  );

  const { t } = useTranslation('navigation', {
    keyPrefix: 'sidebar.popover',
  });

  return (
    <div
      className="mb-lg flex h-auto w-[15rem] animate-fade flex-col content-center justify-between gap-md rounded-xs border-px border-solid border-opacity-subtle p-lg bg-level-02 border-default text-white"
      data-testid={dataTestIds.USER_PROFILE_POPOVER_MENU}
    >
      <p className="text-headline-sm">{name}</p>
      <Separator className="m-0 w-full" />
      {brand.name !== 'WMG' ? (
        <>
          <HoverIconItem
            icon="user"
            text={t('profile')}
            to="/my-profile"
            data-testid={dataTestIds.USER_VIEW_PROFILE_BUTTON}
            onClick={onItemClick}
          />
          {isContainerProfileEnabled ? (
            <HoverIconItem
              icon="user"
              text={t('myProfileNew')}
              to="/my-profile-2"
              data-testid={dataTestIds.USER_VIEW_NEW_PROFILE_BUTTON}
              onClick={onItemClick}
            />
          ) : null}
          <HoverIconItem
            icon="circle-question"
            text={t('support')}
            to={SUPPORT_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onItemClick}
          />
          {!isFeedbackDisabledRoute ? (
            <HoverIconItem
              icon="comment-lines"
              text={t('feedback')}
              to="#feedback_modal"
              data-testid="feedback"
            />
          ) : null}
        </>
      ) : (
        <>
          {isUserManagementEnabled && hasUserManagementReadPermission ? (
            <HoverIconItem
              icon="user-gear"
              text={t('userManagement')}
              to="/users"
              onClick={onItemClick}
            />
          ) : null}
          <HoverIconItem
            icon="comment-lines"
            text={t('feedback')}
            to={WMG_FEEDBACK_URL}
            target="_blank"
            rel="noopener noreferrer"
          />
          <HoverIconItem
            icon="circle-question"
            text={t('support')}
            to={WMG_SUPPORT_URL}
            target="_blank"
            rel="noopener noreferrer"
          />
        </>
      )}

      <Separator className="m-0 w-full" />
      <HoverIconItem
        icon="right-from-bracket"
        text={t('signOut')}
        to="/logout"
        data-testid={dataTestIds.USER_SIGN_OUT_BUTTON}
      />
    </div>
  );
}

SidebarPopoverContent.displayName = 'SidebarPopoverContent';
